import { Box, Card, CardActionArea, CardContent, Theme, ThemeProvider } from "@mui/material";
import { confirmable, ConfirmDialog, createConfirmation } from "react-confirm";

import FlagEncircledIcon from "@/Components/Icons/FlagEncircledIcon";
import GlobeAmericaIcon from "@/Components/Icons/GlobeAmericaIcon";
import { Dialog } from "@/Components/v2/atoms/Dialog";
import { Typography } from "@/Components/v2/atoms/Typography";

import { SpendGeographicGranularity } from "../../api/types";

interface SpendGeographicGranularityConfirmationProps {
  theme: Theme;
}

const SpendGeographicGranularityDialog: ConfirmDialog<
  SpendGeographicGranularityConfirmationProps,
  SpendGeographicGranularity | false
> = ({ show, proceed, theme }) => (
  <ThemeProvider theme={theme}>
    <Dialog
      open={show}
      onClose={() => proceed(false)}
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box p={4}>
        <Typography variant="h5" fontWeight={600} mb={1}>
          Set Spend Input Levels
        </Typography>
        <Typography variant="body1" mb={3}>
          For a national scenario, choose whether to apply budget constraints at the national level
          or by geography.
        </Typography>
        <Box display="flex" gap={3} justifyContent="center" mb={4}>
          <Card
            sx={{
              display: "flex",
              flex: 1,
              minWidth: 300,
              border: "1.5px solid #E0E0E0",
              boxShadow: 0,
            }}
          >
            <CardActionArea
              sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "start" }}
              onClick={() => proceed("all_geographies")}
            >
              <CardContent>
                <FlagEncircledIcon />
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={600}>
                    Set National Constraints
                  </Typography>
                </Box>
                <ul style={{ margin: 0, paddingLeft: 24, listStyleType: "bullet" }}>
                  <li>
                    <Typography>Apply constraints to channels at a national level</Typography>
                  </li>
                  <li>
                    <Typography>
                      Budgets will be split across geographies based on past spend
                    </Typography>
                  </li>
                  <li>
                    <Typography>Geo-level constraints will be disabled</Typography>
                  </li>
                </ul>
              </CardContent>
            </CardActionArea>
          </Card>
          <Card
            sx={{
              display: "flex",
              flex: 1,
              minWidth: 300,
              border: "1.5px solid #E0E0E0",
              boxShadow: 0,
            }}
          >
            <CardActionArea
              sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "start" }}
              onClick={() => proceed("single_geography")}
            >
              {/* Geo = all_geographies */}
              <CardContent>
                <GlobeAmericaIcon />
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={600}>
                    Set Geo Constraints
                  </Typography>
                </Box>
                <ul style={{ margin: 0, paddingLeft: 24, listStyleType: "bullet" }}>
                  <li>
                    <Typography>Apply constraints by channel and geography</Typography>
                  </li>
                  <li>
                    <Typography>Useful for region-specific budget strategies</Typography>
                  </li>
                  <li>
                    <Typography>National-level constraints will be disabled</Typography>
                  </li>
                </ul>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>
      </Box>
    </Dialog>
  </ThemeProvider>
);

const ConfirmableDialog = confirmable(SpendGeographicGranularityDialog);

export const confirmSpendGeographicGranularity = createConfirmation(ConfirmableDialog);
